import apiClient from './client';

export interface DaRegistrareItem {
  conferimento_id: string;
  numero: string | null;
  numero_fir: string | null;
  order_id: string;
  codice_ordine: string;
  customer_id: string;
  customer_ragione_sociale: string;
  categoria: string;
  data_prevista: string | null;
  peso_netto_kg: number | null;
  /** Prefill del blocco trasporto del PROSPETTO (stime di preventivo dall'ordine). */
  costo_trasporto_eur_per_viaggio: number | null;
  numero_trasporti_stimati: number | null;
  cliente_porta_materiale: boolean;
}

/**
 * Uscita di CONFERIMENTO A TERZI non ancora registrata su una fattura passiva.
 * Lista SEPARATA dai conferimenti (endpoint suo): un'uscita non ha ordine ne'
 * categoria, e le colonne che condividono sono tre su otto.
 */
export interface UscitaDaRegistrare {
  uscita_id: string;
  codice: string;
  numero_fir: string;
  data_partenza: string;
  customer_id: string;
  customer_ragione_sociale: string;
  cer_code: string;
  descrizione_rifiuto: string;
  peso_kg: number;
}

/** Body del POST prospetto-pdf (stateless: genera e restituisce il PDF). */
export interface ProspettoTrasportoInput {
  direzione: 'fornitore' | 'ellezeta';
  costo_per_viaggio: number;
  numero_viaggi: number;
}

export interface ProspettoInput {
  customer_id: string;
  conferimento_ids: string[];
  trasporto?: ProspettoTrasportoInput;
}

export interface FatturaPassiva {
  id: string;
  numero_fornitore: string;
  data: string;
  importo: number;
  customer_id: string;
  customer_ragione_sociale: string | null;
  file_name: string | null;
  mime_type: string | null;
  has_file: boolean;
  pagata: boolean;
  data_pagamento: string | null;
  registrata_da: string | null;
  created_at: string;
  conferimento_ids: string[];
  conferimenti: { id: string; numero: string | null }[];
  /** Uscite a terzi registrate sulla stessa fattura (fetta 3). */
  uscite: { id: string; codice: string }[];
}

export async function getDaRegistrare(): Promise<DaRegistrareItem[]> {
  const { data } = await apiClient.get<DaRegistrareItem[]>('/api/fatture-passive/da-registrare');
  return data;
}

export async function getUsciteDaRegistrare(): Promise<UscitaDaRegistrare[]> {
  const { data } = await apiClient.get<UscitaDaRegistrare[]>('/api/fatture-passive/uscite-da-registrare');
  return data;
}

/**
 * Conferimenti e uscite possono viaggiare INSIEME, purche' dello stesso
 * fornitore: a differenza del canale attivo qui non c'e' alcun regime IVA da
 * tenere separato, la fattura passiva e' un documento ricevuto e basta.
 */
export interface RegistraPassivaInput {
  numero_fornitore: string;
  data: string;
  importo: number;
  conferimento_ids: string[];
  uscita_ids: string[];
  pagata: boolean;
  data_pagamento?: string | null;
  file: File;
}

export async function registraFatturaPassiva(input: RegistraPassivaInput): Promise<FatturaPassiva> {
  const fd = new FormData();
  fd.append('numero_fornitore', input.numero_fornitore);
  fd.append('data', input.data);
  fd.append('importo', String(input.importo));
  input.conferimento_ids.forEach((id) => fd.append('conferimento_ids', id));
  input.uscita_ids.forEach((id) => fd.append('uscita_ids', id));
  fd.append('pagata', String(input.pagata));
  if (input.data_pagamento) fd.append('data_pagamento', input.data_pagamento);
  fd.append('file', input.file);
  const { data } = await apiClient.post<FatturaPassiva>('/api/fatture-passive', fd);
  return data;
}

export async function listFatturePassive(): Promise<FatturaPassiva[]> {
  const { data } = await apiClient.get<FatturaPassiva[]>('/api/fatture-passive');
  return data;
}

export async function updateFatturaPassiva(
  id: string,
  body: { numero_fornitore?: string; data?: string; importo?: number; pagata?: boolean; data_pagamento?: string | null },
): Promise<FatturaPassiva> {
  const { data } = await apiClient.patch<FatturaPassiva>(`/api/fatture-passive/${id}`, body);
  return data;
}

export async function deleteFatturaPassiva(id: string): Promise<{ deleted: boolean; id: string }> {
  const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(`/api/fatture-passive/${id}`);
  return data;
}

export async function getFatturaPassiva(id: string): Promise<FatturaPassiva> {
  const { data } = await apiClient.get<FatturaPassiva>(`/api/fatture-passive/${id}`);
  return data;
}

export async function getFilePassivaBlob(id: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/fatture-passive/${id}/file`, { responseType: 'blob' });
  return data as Blob;
}
